import { useCallback, useEffect, useState } from 'react';
import { pageContentService } from '../../api/pageContentService';

/**
 * Load / edit / save the JSON document of one storefront page.
 *
 * `setSection` patches a single top-level section, which is what every field in
 * the editors does — the whole document is then PUT back in one request.
 */
export function usePageEditor(key) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await pageContentService.get(key);
      setContent(data.content || {});
      setUpdatedAt(data.updated_at || null);
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Impossible de charger le contenu de la page.'
      );
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  const setSection = useCallback((section, patch) => {
    setContent((prev) => ({
      ...prev,
      [section]: { ...(prev?.[section] || {}), ...patch },
    }));
  }, []);

  /** Replace a whole top-level section (used for list-only sections like `stats`). */
  const replaceSection = useCallback((section, value) => {
    setContent((prev) => ({ ...prev, [section]: value }));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const data = await pageContentService.update(key, content);
      setContent(data.content || content);
      setUpdatedAt(data.updated_at || null);
      setMessage('Contenu enregistré. La page du site est mise à jour.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(
        err?.response?.data?.message || "Échec de l'enregistrement. Veuillez réessayer."
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  }, [key, content]);

  const reset = useCallback(async () => {
    const confirmed = window.confirm(
      'Restaurer le contenu d\'origine de cette page ? Vos modifications seront perdues.'
    );
    if (!confirmed) return;

    setResetting(true);
    setMessage('');
    setError('');
    try {
      const data = await pageContentService.reset(key);
      setContent(data.content || {});
      setUpdatedAt(null);
      setMessage("Contenu réinitialisé au texte d'origine.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err?.response?.data?.message || 'Échec de la réinitialisation.');
    } finally {
      setResetting(false);
    }
  }, [key]);

  return {
    content,
    setContent,
    setSection,
    replaceSection,
    loading,
    saving,
    resetting,
    message,
    error,
    setError,
    updatedAt,
    save,
    reset,
  };
}
